<template>
  <div class="flex flex-col proxima_nova text_color gap-10">
    <div class="flex flex-col">
      <h1 class="text-xl font-semibold">Sender Details</h1>
      <span class="secondary_color text-xs">
        Add and manage sender email addresses that will be used for sending email campaigns. Verified emails ensure better deliverability.
      </span>
    </div>
    <!-- <div class="sender_name">
      <span class="text-[13px] secondary_color">Default Sender Name</span>
      <a-input
        v-model:value="senderName"
        placeholder="Enter sender name"
        class="input_medium w-1/3 mt-2"
      />
    </div> -->
    <div class="flex flex-col gap-2">
      <div
        class="sender_emails flex flex-row justify-between text-center items-center"
      >
        <span class="font-semibold">Sender Emails</span>
        <a-button class="secondary_medium" @click="addSenderEmail"
          >Add Sender Email</a-button
        >
      </div>
      <div
        v-if="loading"
        class="flex flex-col items-center justify-center gap-2 mt-20"
      >
        <SkeletonLoader v-if="loading" :page="loaderPages.TABLE" />
      </div>
      <div v-else-if="senderDetails.length === 0">
        <EmptyScreen
          source="sender-emails-empty"
          text="You Haven't Added Any Sender Emails Yet!"
        />
      </div>
      <div v-else>
        <CustomTable
          :headers="senderDetailsHeader"
          :data="senderDetails"
          height="calc(100vh - (270px + var(--banner-height)))"
          :show-actions-column="true"
          :actions="actionOptions"
          :custom-column-render="customColumnRender"
        />
      </div>
    </div>

    <CustomModal
      v-model:visible="isModalVisible"
      title="Add Sender Email"
      width="36vw"
    >
      <span class="secondary_color text-[14px]">Sender Email Id</span>
      <a-input
        v-model:value="emailId"
        placeholder="Enter email"
        class="input_medium mt-1"
      />
      <template #footer>
        <a-button
          class="primary_medium"
          @click="addSenderMail"
          :loading="loading"
          >Add Email</a-button
        >
      </template>
    </CustomModal>

    <CustomModal
      v-model:visible="isDeleteModalVisible"
      title="Delete Email"
      width="36vw"
    >
      <span>Are you sure you want to delete this sender email?</span>
      <template #footer>
        <a-button
          class="primary_medium"
          @click="deleteSenderEmail"
          :loading="deleteLoading"
          >Delete</a-button
        >
      </template>
    </CustomModal>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from "vue";
import CustomTable from "@/src/components/custom_table_components/CustomTable.vue";
import { senderDetailsHeader } from "@/src/constants/campaignInEngageConstants.js";
import { imageNames } from "@/src/constants/imageConstants.js";
import CustomModal from "@/src/components/CustomModal.vue";
import { message } from "ant-design-vue";
import VerifyButtonComponent from "@/src/components/email_campaign_settigns/VerifyButtonComponent.vue";
import SuperCampaignsService from "@/src/api/SuperCampaignsService.js";
import { loaderPages } from "@/src/constants/loaderConstants.js";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import EmptyScreen from "@/src/components/EmptyScreen.vue";

const emailId = ref("");
const senderName = ref("");
const loading = ref(false);
const deleteLoading = ref(false);
const senderDetails = ref([]);
const isModalVisible = ref(false);
const isDeleteModalVisible = ref(false);
const selectedEmail = ref(null);

const fetchSenderDetails = async () => {
  try {
    loading.value = true;
    const response = await SuperCampaignsService.getSenderEmails();
    if (response?.success) {
      if (response.sender_emails && Array.isArray(response.sender_emails)) {
        senderDetails.value = response.sender_emails.map((emailObj) => ({
          email: emailObj.email,
          status: emailObj.status,
        }));
      } else {
        senderDetails.value = [];
      }
      
      if (response.sender_name) {
        senderName.value = response.sender_name;
      }
    } else {
      message.error("Failed to fetch sender details");
    }
  } catch (error) {
    message.error("An error occurred while fetching sender details");
    console.error("Error while fetching sender details: ", error);
  } finally {
    loading.value = false;
  }
};

const addSenderEmail = () => {
  emailId.value = "";
  isModalVisible.value = true;
};

const showDeleteModal = (row) => {
  selectedEmail.value = row.email;
  isDeleteModalVisible.value = true;
};

const actionOptions = [
  {
    label: "Delete",
    handler: (row) => showDeleteModal(row),
    icon: imageNames.DELETE_GREY,
  },
];

const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const addSenderMail = async () => {
  if (!emailId.value?.trim()) {
    message.error("Please enter an email address");
    return;
  }

  if (!validateEmail(emailId.value.trim())) {
    message.error("Please enter a valid email address");
    return;
  }

  try {
    loading.value = true;
    const payload = {
      sender_email: emailId.value.trim(),
    };
    
    const response = await SuperCampaignsService.saveSenderEmail(payload);
    if (response?.success) {
      message.success("Sender Email Added Successfully");
      await fetchSenderDetails();
    } else if (response?.message) {
      message.error(response?.message);
    } else {
      message.error("Failed to add sender email");
    }
  } catch (error) {
    message.error("An error occurred while adding email");
    console.error("Error while adding sender email: ", error);
  } finally {
    loading.value = false;
    isModalVisible.value = false;
  }
};

const deleteSenderEmail = async () => {
  try {
    deleteLoading.value = true;
    const response = await SuperCampaignsService.deleteSenderEmail(
      selectedEmail.value,
    );
    if (response?.success) {
      message.success("Sender Email Deleted Successfully");
      await fetchSenderDetails();
    } else if (response?.message) {
      message.error(response?.message);
    } else {
      message.error("Failed to delete sender email");
    }
  } catch (error) {
    message.error("An error occurred while deleting email");
    console.error("Error while deleting sender email: ", error);
  } finally {
    deleteLoading.value = false;
    isDeleteModalVisible.value = false;
  }
};

const customColumnRender = {
  verify: VerifyButtonComponent,
};

const handleRefreshData = () => {
  fetchSenderDetails();
};

onMounted(() => {
  fetchSenderDetails();
  window.addEventListener("refresh-sender-data", handleRefreshData);
});

onUnmounted(() => {
  window.removeEventListener("refresh-sender-data", handleRefreshData);
});
</script>
